/**
 * The product-usage tracker's public surface.
 *
 * A module singleton rather than a React context: a context would re-render the tree
 * on every event and force every call site to live under a provider. `track()` is
 * callable from anywhere, including outside React.
 *
 * The contract every call site relies on: **`track()` never throws and never
 * awaits.** Telemetry is not a reason for a screen to fail to render, so every
 * failure path here ends in a swallowed exception. It is also a no-op when signed
 * out — there is nobody to attribute the event to, and the paywall is not a member
 * journey until somebody is behind it.
 *
 * The local kill switch is for development only. `VITE_*` is baked in at build time
 * and the native shell ships `dist/`, so a client-side flag would need an App Store
 * release to take effect; the real switch is `ANALYTICS_ENABLED` on the API.
 */

import {
  configureQueue,
  discardQueue,
  enqueue,
  flush,
  persistNow,
  restoreQueue,
  startFlushTimer,
  stopFlushTimer,
} from "~/lib/analytics-queue";
import {
  currentSession,
  discardSession,
  persist as persistSession,
  restoreSession,
} from "~/lib/analytics-session";
import { isAppActive, onAppStateChange } from "~/lib/app-state";
import { platform } from "~/lib/capacitor";
import { useAuthStore } from "~/store/auth-store";
import {
  AnalyticsEventName,
  type AnalyticsProps,
  type AnalyticsSubjectType,
} from "~/types/analytics";

const LOCAL_KILL_SWITCH = import.meta.env.VITE_ANALYTICS_DISABLED === "true";

export interface TrackOptions {
  screen?: string | null;
  subjectType?: AnalyticsSubjectType | null;
  subjectId?: string | null;
  /** The slug. It is what identifies content across both locales. */
  subjectKey?: string | null;
  contentFormat?: string | null;
  /** Milliseconds for dwell, percent for progress. */
  value?: number | null;
  props?: AnalyticsProps;
}

let started = false;
let disposeAppState: (() => void) | undefined;
let currentScreen: string | null = null;
let backgroundedAt: number | null = null;

/** Tell the tracker which route pattern is on screen. */
export function setCurrentScreen(pattern: string | null): void {
  currentScreen = pattern;
}

/**
 * Record an event. Fire-and-forget by design; see the module docblock.
 */
export function track(
  name: AnalyticsEventName,
  options: TrackOptions = {},
): void {
  try {
    if (LOCAL_KILL_SWITCH) return;
    const { status, user } = useAuthStore.getState();
    if (status !== "authenticated" || !user) return;

    const session = currentSession(user.id);
    if (session.isNew && name !== AnalyticsEventName.SessionStart) {
      // A session has to announce itself, whatever event happened to open it.
      enqueueEvent(AnalyticsEventName.SessionStart, session.id, {});
    }
    enqueueEvent(name, session.id, options);
  } catch {
    // Telemetry is never a reason for a screen not to render.
  }
}

function enqueueEvent(
  name: AnalyticsEventName,
  sessionId: string,
  options: TrackOptions,
): void {
  enqueue({
    id: crypto.randomUUID(),
    name,
    occurred_at: new Date().toISOString(),
    session_id: sessionId,
    screen: options.screen !== undefined ? options.screen : currentScreen,
    subject_type: options.subjectType ?? null,
    subject_id: options.subjectId ?? null,
    subject_key: options.subjectKey ?? null,
    content_format: options.contentFormat ?? null,
    value: options.value ?? null,
    props: options.props ?? {},
  });
}

/**
 * Wire the tracker to an authenticated member.
 *
 * Replays anything a previous run left queued, then starts the flush timer and the
 * foreground listener. Idempotent, so React's double effect invocation in
 * development cannot start two of anything.
 */
export async function initializeAnalytics(
  userId: string,
  locale: string,
): Promise<void> {
  try {
    if (started || LOCAL_KILL_SWITCH) return;
    started = true;

    configureQueue(userId, {
      locale,
      platform: platform() as "ios" | "android" | "web",
      app_version: import.meta.env.VITE_APP_VERSION ?? null,
    });
    await restoreSession(userId);
    await restoreQueue(userId);

    disposeAppState = onAppStateChange((active) => {
      void handleAppStateChange(active);
    });
    startFlushTimer();

    track(AnalyticsEventName.AppForeground);
    await flush("boot");
  } catch {
    // A tracker that failed to start is a tracker that records nothing, which is a
    // strictly better outcome than an app that failed to start.
  }
}

async function handleAppStateChange(active: boolean): Promise<void> {
  try {
    if (active) {
      const backgroundMs =
        backgroundedAt === null ? null : Date.now() - backgroundedAt;
      backgroundedAt = null;
      track(AnalyticsEventName.AppForeground, {
        props: backgroundMs === null ? {} : { background_ms: backgroundMs },
      });
      return;
    }

    backgroundedAt = Date.now();
    track(AnalyticsEventName.AppBackground);
    // Persist first, flush second. The OS may kill a backgrounded app at any point,
    // and there is no guarantee the request completes — but the write is what makes
    // the events survive to the next launch.
    await persistSession();
    await persistNow();
    await flush("background");
  } catch {
    // Nothing useful to do here; the queue is already on disk.
  }
}

/** Forget everything about the signed-out member. */
export async function discardAnalytics(): Promise<void> {
  try {
    started = false;
    backgroundedAt = null;
    currentScreen = null;
    disposeAppState?.();
    disposeAppState = undefined;
    stopFlushTimer();
    await discardSession();
    await discardQueue();
  } catch {
    // Best effort: the next sign-in discards a foreign queue on restore anyway.
  }
}

/** Flush on a route change, rate-limited inside the queue. */
export function flushOnNavigation(): void {
  void flush("navigation");
}

export { isAppActive };
